import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Level, Logger } from '@nsalaun/ng-logger';
import { install as installResizeObserverPolyfill } from 'resize-observer';
import { map } from 'rxjs/operators';

import { InfoDialogData } from '@svg-frontends/dialog/components/info-dialog/info-dialog.component';
import { DialogLevel } from '@svg-frontends/dialog/enums/dialog-level.enum';
import { StandardDialogService } from '@svg-frontends/dialog/services/standard-dialog.service';
import { EnrichedError } from '@svg-frontends/error/models/enriched-error.model';
import { ErrorHandlingService } from '@svg-frontends/error/services/error-handling.service';
import { LoggingService } from '@svg-frontends/logging/services/logging.service';
import { AuthRefreshService } from '@svg-frontends/session/authentication/services/auth-refresh.service';
import { AsiContext } from '@svg-frontends/session/context/models/asi-context.model';
import { KeycloakTokenClaims } from '@svg-frontends/session/context/models/user-token-claims.model';
import { AsiContextService } from '@svg-frontends/session/context/services/asi-context.service';
import { CustomCommonStringsService } from '@svg-frontends/theming/clarity-wrapper.module';
import { UpdateAppWorkerService } from '@svg-frontends/update-app-worker/update-app-worker.service';

import '../../../../init-dayjs';
import { ENVIRONMENT } from '../environments/environment';
import { ClarityInitService } from './clarity-init.service';

@UntilDestroy()
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-root',
	styleUrls: ['./app.component.scss'],
	templateUrl: './app.component.html',
	standalone: false,
})
export class AppComponent {
	constructor(
		asiContextService: AsiContextService,
		authRefreshService: AuthRefreshService<AsiContext, KeycloakTokenClaims>,
		clarityInitService: ClarityInitService,
		customCommonStringsService: CustomCommonStringsService,
		loggingService: LoggingService,
		logger: Logger,
		updateAppWorkerService: UpdateAppWorkerService,
		private errorHandlingService: ErrorHandlingService,
		private standardDialogService: StandardDialogService,
	) {
		authRefreshService.init();
		clarityInitService.init();
		asiContextService.init();
		customCommonStringsService.init();
		loggingService.init();
		logger.info(`Started myASI application and enabled Logging with Level ${Level[logger.level]}`);

		this.initErrorDialogHandling();

		if (ENVIRONMENT.enabledUpdateServiceWorker) {
			updateAppWorkerService.init();
			logger.debug(
				"UpdateAppWorkerService initialized and actively checking for updates in the background to ensure you're always running the latest version.",
			);
		} else {
			logger.debug('Failed to initialize UpdateAppWorkerService. Unable to check for updates in the background');
		}

		// polyfill for older IOS devices not supporting ResizeObserver (WEB-2140)
		if (!window.ResizeObserver) installResizeObserverPolyfill();
	}

	private static getInfoDialogDataFromError(enrichedError: EnrichedError): InfoDialogData {
		return {
			body: enrichedError.displayMessages.join(' '),
			level: DialogLevel.DANGER,
			submitButton: 'Ok',
			title: 'Ups, es ist ein Fehler aufgetreten!',
		};
	}

	private initErrorDialogHandling(): void {
		this.errorHandlingService
			.getErrorForDialogObservable()
			.pipe(
				map((enrichedError: EnrichedError) => AppComponent.getInfoDialogDataFromError(enrichedError)),
				untilDestroyed(this),
			)
			.subscribe((data: InfoDialogData) => this.standardDialogService.showInfoDialog(data));
	}
}
